/**
 * Aceleres: identidade do PARTICIPANTE de um grupo do WhatsApp.
 *
 * Numa conversa de grupo o Chatwoot tem um contato só — o grupo — e portanto
 * todas as mensagens recebidas compartilham o mesmo `sender.id`. Sem uma
 * identidade por participante, o painel tratava o grupo inteiro como uma
 * pessoa: as bolhas de gente diferente entravam no mesmo bloco agrupado e o
 * único vestígio de quem falou era um prefixo cravado no texto da mensagem.
 *
 * Quem de fato enviou sempre esteve em `content_attributes` — este módulo é o
 * lugar único que lê esses campos, para que agrupamento (MessageList) e
 * apresentação (useDisplaySenderName) nunca discordem sobre quem é quem.
 *
 * As três ingestões existentes gravam o participante com nomes diferentes, e o
 * payload chega ora em camelCase (serializer do Chatwoot) ora em snake_case
 * (gravado direto pela ponte WAME) — daí a leitura tolerante em cada campo.
 */

const firstPresent = (...values) => {
  const found = values.find(
    value =>
      value !== null && value !== undefined && String(value).trim() !== ''
  );
  return found === undefined ? null : String(found).trim();
};

const attributeOf = (contentAttributes, camel, snake) =>
  firstPresent(contentAttributes?.[camel], contentAttributes?.[snake]);

/**
 * A mensagem pertence a uma conversa de grupo do WhatsApp?
 * @param {object} contentAttributes
 * @returns {boolean}
 */
export function isGroupMessage(contentAttributes) {
  const isGroup = contentAttributes?.isGroup ?? contentAttributes?.is_group;
  if (isGroup === true || isGroup === 'true') return true;

  // Ingestões antigas nem sempre gravaram `is_group`, mas o JID do grupo é
  // igualmente conclusivo: só thread de grupo termina em @g.us.
  const jid = attributeOf(
    contentAttributes,
    'wameRemoteJid',
    'wame_remote_jid'
  );
  const groupJid = attributeOf(
    contentAttributes,
    'whatsappGroupJid',
    'whatsapp_group_jid'
  );
  return !!(jid?.endsWith('@g.us') || groupJid?.endsWith('@g.us'));
}

/**
 * JID bruto do participante que enviou a mensagem.
 * @param {object} contentAttributes
 * @returns {string|null}
 */
export function groupParticipantJid(contentAttributes) {
  return (
    attributeOf(
      contentAttributes,
      'wameGroupParticipantJid',
      'wame_group_participant_jid'
    ) ??
    attributeOf(contentAttributes, 'whatsappSenderJid', 'whatsapp_sender_jid')
  );
}

/**
 * Chave estável do participante, usada para decidir se duas mensagens são da
 * MESMA pessoa.
 *
 * O sufixo `:<n>` de um JID é o identificador do APARELHO (`...935:49@...`), não
 * da pessoa: o mesmo participante mandando do celular e do WhatsApp Web produz
 * sufixos diferentes. Mantê-lo na chave quebraria o bloco visual a cada troca
 * de dispositivo. Já o domínio permanece: `@lid` e `@s.whatsapp.net` são
 * espaços de identificação distintos e o mesmo número em cada um não pode ser
 * fundido às cegas.
 *
 * @param {object} contentAttributes
 * @returns {string|null}
 */
export function groupParticipantKey(contentAttributes) {
  const jid = groupParticipantJid(contentAttributes);
  if (jid) {
    const [user, domain] = jid.split('@');
    const withoutDevice = String(user || '').split(':')[0];
    if (withoutDevice) {
      return domain ? `${withoutDevice}@${domain}` : withoutDevice;
    }
  }

  // Sem JID, o nome é a única âncora disponível. É mais fraco (dois
  // participantes homônimos colidem), mas ainda separa o caso comum.
  const name = attributeOf(
    contentAttributes,
    'wameGroupSenderName',
    'wame_group_sender_name'
  );
  return name ? `name:${name.toLowerCase()}` : null;
}

/**
 * Atributos do grupo guardados no CONTATO da conversa.
 *
 * A ponte WAME grava `is_whatsapp_group` e o roster em `custom_attributes`,
 * mas parte da UI procurava só em `additional_attributes` — e, encontrando um
 * hash vazio, concluía que a conversa não era de grupo. Ler os dois lugares
 * aqui evita que a resposta dependa de qual campo a ingestão usou.
 *
 * @param {object} conversation
 * @returns {object} atributos onde o marcador de grupo pode estar
 */
export function conversationGroupAttributes(conversation) {
  if (!conversation) return {};

  const sources = [
    conversation.meta?.sender?.custom_attributes,
    conversation.meta?.sender?.additional_attributes,
    conversation.meta?.contact?.custom_attributes,
    conversation.meta?.contact?.additional_attributes,
    conversation.custom_attributes,
    conversation.additional_attributes,
  ];

  return sources.reduce(
    (merged, source) =>
      source && typeof source === 'object' ? { ...source, ...merged } : merged,
    {}
  );
}

/**
 * A CONVERSA é um grupo do WhatsApp?
 * @param {object} conversation
 * @returns {boolean}
 */
export function isGroupConversation(conversation) {
  const attributes = conversationGroupAttributes(conversation);
  const flag = attributes.is_whatsapp_group ?? attributes.isWhatsappGroup;
  if (flag === true || flag === 'true') return true;

  const jid = String(attributes.remote_jid ?? attributes.remoteJid ?? '');
  return jid.endsWith('@g.us');
}

/**
 * Roster de participantes que a ponte WAME sincroniza no contato do grupo.
 * @param {object} conversation
 * @returns {Array<object>}
 */
export function groupRoster(conversation) {
  const attributes = conversationGroupAttributes(conversation);
  const group = attributes.whatsapp_group ?? attributes.whatsappGroup;
  const participants = group?.participants;

  return Array.isArray(participants) ? participants : [];
}

const DIGITS_ONLY = /^\+?[\d\s()-]+$/;

const isHumanName = value =>
  !!value && !DIGITS_ONLY.test(value) && !value.startsWith('+');

/**
 * Formata um número brasileiro para leitura. Devolve o original quando não
 * reconhece o formato — nunca inventa dígito.
 */
const formatPhone = digits => {
  const clean = String(digits).replace(/\D/g, '');
  if (clean.length === 13 && clean.startsWith('55')) {
    return `+55 ${clean.slice(2, 4)} ${clean.slice(4, 9)}-${clean.slice(9)}`;
  }
  if (clean.length === 12 && clean.startsWith('55')) {
    return `+55 ${clean.slice(2, 4)} ${clean.slice(4, 8)}-${clean.slice(8)}`;
  }
  return null;
};

/**
 * Localiza o participante no roster do grupo.
 *
 * O casamento precisa ser por id E por telefone porque as duas pontas nomeiam
 * a mesma pessoa de formas diferentes: o roster lista todo mundo por `@lid`
 * (`30352567464091@lid`), enquanto a mensagem traz ora esse mesmo `@lid`, ora
 * o JID de telefone (`555196235388@s.whatsapp.net`). Comparar só o id perderia
 * exatamente os participantes que mandam pelo número.
 */
const findInRoster = (jid, roster) => {
  if (!jid || !roster.length) return null;

  const [user] = jid.split('@');
  const bare = String(user || '').split(':')[0];
  if (!bare) return null;

  return (
    roster.find(participant => {
      const id = String(participant?.id || '')
        .split('@')[0]
        .split(':')[0];
      return id && id === bare;
    }) ??
    roster.find(participant => {
      const phone = String(participant?.phone_number || '').replace(/\D/g, '');
      return phone && phone === bare;
    }) ??
    null
  );
};

/**
 * Nome a exibir para o participante.
 *
 * O `~` que o WhatsApp prefixa em nomes de perfil público ("~Ângelo") é ruído
 * de protocolo e sai daqui.
 *
 * O roster tem precedência sobre o `pushName` da mensagem por uma razão de
 * estabilidade: o `pushName` viaja mensagem a mensagem e falta em algumas
 * (mídia reenviada, participante com privacidade restrita). Se cada bolha
 * escolhesse por si, a MESMA pessoa apareceria como "Bruno" numa e como o
 * número cru na seguinte, e o bloco visual se quebraria no meio da fala.
 *
 * @param {object} contentAttributes  content_attributes da mensagem
 * @param {Array<object>} [roster]    participantes sincronizados no contato
 * @returns {string|null}
 */
export function groupParticipantName(contentAttributes, roster = []) {
  const raw =
    attributeOf(
      contentAttributes,
      'wameGroupSenderName',
      'wame_group_sender_name'
    ) ??
    firstPresent(
      contentAttributes?.aceleresGroupSender?.resolved,
      contentAttributes?.aceleres_group_sender?.resolved
    );

  const pushName = raw ? raw.replace(/^~\s*/, '').trim() : null;

  const jid = groupParticipantJid(contentAttributes);
  const entry = findInRoster(jid, roster);
  const rosterName = entry?.name
    ? String(entry.name).replace(/^~\s*/, '').trim()
    : null;

  if (isHumanName(rosterName)) return rosterName;
  if (isHumanName(pushName)) return pushName;

  // Ninguém tem nome de gente: exibimos o telefone. O roster é a única fonte
  // que sabe o número por trás de um `@lid` — que é um id interno do WhatsApp,
  // não um telefone, e apareceria como "89495005331680" sem essa tradução.
  const rosterPhone = String(entry?.phone_number || '').replace(/\D/g, '');
  if (rosterPhone) return formatPhone(rosterPhone) ?? `+${rosterPhone}`;

  const digits = pushName || (jid ? jid.split('@')[0].split(':')[0] : null);
  if (!digits) return null;

  if (jid?.endsWith('@lid')) return pushName || digits;

  return formatPhone(digits) ?? digits;
}

/**
 * Prefixos de remetente cravados no corpo da mensagem por ingestões anteriores.
 *
 * O nome do participante passou a viver só em `content_attributes`, mas o
 * histórico já gravado continua com o prefixo dentro do texto. Removê-lo na
 * renderização faz mensagem antiga e nova ficarem idênticas na tela sem
 * reescrever o banco.
 *
 * Cada padrão exige o nome que a própria mensagem declara — nunca um "qualquer
 * coisa antes de dois-pontos" —, de modo que uma mensagem legítima como
 * "Combinado: amanhã às 9h" jamais é decapitada.
 *
 * @param {string} content
 * @param {object} contentAttributes
 * @returns {string} conteúdo sem o prefixo de remetente
 */
export function stripGroupSenderPrefix(content, contentAttributes) {
  if (typeof content !== 'string' || !content) return content;
  if (!isGroupMessage(contentAttributes)) return content;

  const declared = attributeOf(
    contentAttributes,
    'wameGroupSenderName',
    'wame_group_sender_name'
  );

  if (declared) {
    const escaped = declared.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Ponte WAME ("Nome:\n"), initializer do Chatwoot ("[Nome]\n") e a forma
    // já reescrita em negrito ("**Nome:**").
    const legacy = new RegExp(
      `^(?:\\*\\*${escaped}:\\*\\*|\\[${escaped}\\]|${escaped}:)\\s*\\n?`
    );
    if (legacy.test(content)) {
      return content.replace(legacy, '');
    }
  }

  // Formato cru do Evolution: "**+55 4584339084 - Deus E Bom:**". O padrão é
  // específico o bastante (negrito + número + hífen + dois-pontos) para não
  // colidir com texto escrito por gente.
  const evolution = /^\*\*\s*\+?\d[\d\s]*?\s*-\s*.+?\s*:\*\*\s*\n?/;
  if (evolution.test(content)) {
    return content.replace(evolution, '');
  }

  return content;
}
